import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { FunctionalTrainerBuilder } from "../../functionalTrainerBuilder"
import { createLatBarClip } from "../ftSharedParts"
import { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
import type { SlingerXContext } from "./slingerXParts"
import {
  buildFrontPulley,
  buildBackPulley,
  buildWeightStack,
  buildSeat,
  buildBottomSwivelPulley,
  buildSwivelHanger,
} from "./slingerXParts"

export class SlingerXBuilder extends FunctionalTrainerBuilder {

  // isRML: RML/FML vs RM/FM line — drives slot names + per-line mesh-calibration offsets. slingerLine: Blender file prefix ('FML' vs 'FM').
  protected isRML: boolean
  protected slingerLine: string

  constructor(config: RackOptions) {
    super(config)
    this.isRML = config.line === 'RML'
    this.slingerLine = config.line === 'RM' ? 'FM' : 'FML'
  }

  private createContext(materials: MaterialInfo, left?: boolean): SlingerXContext {
    return { config: this.getSideConfig(left), materials, isRML: this.isRML, slingerLine: this.slingerLine }
  }

  // Returns config with per-side overrides applied; falls back to flat fields when unset or left is undefined.
  private getSideConfig(left?: boolean): RackOptions {
    if (left === undefined) return this.config

    const side = this.config.slingerXSides?.[left ? 'left' : 'right']

    return {
      ...this.config,
      weightStackAmount: side?.weightStackAmount ?? this.config.weightStackAmount,
     
      seatSelection   : side?.seatSelection,
      seatLowRowKit   : side?.seatLowRowKit,
      swivelPulley    : side?.swivelPulley,
      latBarSelection : side?.latBarSelection ?? this.config.latBarSelection,
      lowRowSelection : side?.lowRowSelection ?? this.config.lowRowSelection,
    }
  }

  // MARK: Assemble Parts
  protected addFunctionalTrainerComponents(part: PartDefinition): void {
    const materials = this.materialBuilder.createMainMaterial()

    if (this.config.leftSide) {
      this.addSide(part, materials, true)
    }

    if (this.config.rightSide) {
      this.addSide(part.slots['other-side'] as PartDefinition, materials, false)
    }
  }

  // MARK: Cave 2.0 Assembly
  // Returns Slinger X parts for external Cave attachment (mirrors IndyBuilder.createCaveAssembly); frontUpright is the Cave cable upright.
  public createCaveAssembly(left: boolean, frontUpright: PartDefinition) {
    const context = this.createContext(this.materialBuilder.createMainMaterial())

    const swivelPulley = this.attachSwivelPulley(frontUpright, context, left)

    return {
      frontPulley : buildFrontPulley(context, left, this.buildLatBarClip(left)),
      backPulley  : buildBackPulley(context, left),
      weightStack : buildWeightStack(context, left),
      swivelPulley,
    }
  }

  // MARK: Side Assembly
  private addSide(frontUpright: PartDefinition, materials: MaterialInfo, left: boolean): void {
    const context = this.createContext(materials, left)

    // Front pulley → front upright. Array-append so a pullup / front crossmember still renders.
    frontUpright.slots.top = ([] as PartDefinition[]).concat(frontUpright.slots.top ?? [], buildFrontPulley(context, left, this.buildLatBarClip(left)))

    // Array-append so the base rack's safety accessory (safety-high) isn't clobbered when a seat is also present.
    frontUpright.slots['safety-high'] = ([] as PartDefinition[]).concat(frontUpright.slots['safety-high'] ?? [], context.config.seatSelection ? [buildSeat(context, left)] : [])
    this.attachSwivelPulley(frontUpright, context, left)

    // 6-post hangs back pulley + stack off the rear storage-row crossmembers; 4-post has none, so they attach to the front upright's own (Indy attachToHalfRack 24" slots).
    const topCrossmember    = this.config.sixPost ? this.getTopStorageCrossmember(frontUpright) : this.getTopCrossmember(frontUpright)
    const bottomCrossmember = this.config.sixPost ? this.getBottomStorageCrossmember(frontUpright) : this.getBottomCrossmember(frontUpright)

    // RML uses the shared `slinger` slot for both parts; RM uses `side_2` (back pulley) and `side_3` (stack).
    const backPulleySlot = this.isRML ? 'slinger' : 'side_2'
    const stackSlot      = this.isRML ? 'slinger' : 'side_3'

    topCrossmember.slots[backPulleySlot] = buildBackPulley(context, left)
    bottomCrossmember.slots[stackSlot] = buildWeightStack(context, left)
  }

  // Bottom swivel pulley + hanger — shared by addSide and createCaveAssembly, attached identically.
  private attachSwivelPulley(frontUpright: PartDefinition, context: SlingerXContext, left: boolean): PartDefinition | undefined {
    const swivelPulleySlotName = this.isRML ? 'storage' : 'fxt'
    const swivelPulley = context.config.swivelPulley ? buildBottomSwivelPulley(context, left) : undefined

    frontUpright.slots[swivelPulleySlotName] = swivelPulley ? [swivelPulley] : []

    if (context.config.swivelPulley) {
      frontUpright.slots['jcup-high'] = ([] as PartDefinition[]).concat(frontUpright.slots['jcup-high'] ?? [], buildSwivelHanger(context, left))
    }

    return swivelPulley
  }

  // Lat bar clip needs materialBuilder (builder-only), so it's resolved here and passed into the front pulley — mirrors StandardFMBuilder.
  private buildLatBarClip(left: boolean): PartDefinition {
    return createLatBarClip(this.getLatBarMaterial(left), this.getSideConfig(left).latBarSelection)
  }

  // Mirrors Cave's CaveCableSystemBuilder.createLatBarAttachment: materials.left/right.latpulldown (set by createSlingerXConfig's per-side getCommonAccessorySelections calls) take priority; falls back to the flat, non-namespaced materialBuilder result for createCaveAssembly's own single-side config.
  private getLatBarMaterial(left: boolean): MaterialInfo {
    const key = left ? 'left' : 'right'

    return (this.config.materials?.[key]?.latpulldown as MaterialInfo) ?? this.materialBuilder.createLatBarMaterial()
  }

  // MARK: Crossmember Navigation
  private getTopStorageCrossmember(part: PartDefinition): PartDefinition {
    const secondRowUpright = this.getBottomCrossmember(part).slots.back as PartDefinition

    return secondRowUpright.slots['crossmember-top'] as PartDefinition
  }

  private getBottomStorageCrossmember(part: PartDefinition): PartDefinition {
    const secondRowUpright = this.getBottomCrossmember(part).slots.back as PartDefinition

    return secondRowUpright.slots['crossmember-bottom'] as PartDefinition
  }

  // FM abstract method stubs (never invoked)
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] { return [] }
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition { return { mesh: '', slots: {} } }
  protected addResistanceSystem(backUpright: PartDefinition, materials: MaterialInfo): void {}
  protected attachToHalfRack(sideMeshes: PartDefinition[], frontBottomPulley: PartDefinition, topCrossmember: PartDefinition, bottomCrossmember: PartDefinition, left: boolean): void {}
  protected attachToFullRack(sideMeshes: PartDefinition[], frontBottomPulley: PartDefinition, topCrossmember: PartDefinition, bottomCrossmember: PartDefinition, materials: MaterialInfo, left: boolean): void {}
  protected getModelPrefix(): string { return 'FM' }
}
